import { INTERACTIVE_SELECTOR } from './utils/utils.js';

/**
 * handles window and viewport events for the carousel
 */
export default class WindowEvents {
	/**
	 * creates a new window events instance
	 * @param {object} ctx - shared module context
	 * @param {object} ctx.emitter
	 * @param {object} ctx.events
	 * @param {HTMLElement} ctx.carousel
	 * @param {HTMLElement} ctx.viewport
	 */
	constructor(ctx) {
		const _ = this;

		// save ctx and frequently used refs
		_.ctx = ctx;
		_.carousel = ctx.carousel;
		_.viewport = ctx.viewport;

		// track viewport width to ignore height-only changes (mobile Safari bottom bar)
		_.lastViewportWidth = null;

		// per-element width cache for the unified observer (avoids height-only rebuilds)
		_.observedWidths = new WeakMap();
		// currently observed visibility-bounds element (when different from viewport)
		_.boundsElement = null;

		// helper: check if an event originated inside the carousel
		_.isInsideCarousel = (e) => {
			const path = e?.composedPath?.() || [];
			if (path.length) return path.includes(_.carousel);
			return _.carousel.contains(e?.target);
		};

		// define all event handlers in one object
		_.handlers = {
			// emit a canonical window:resize
			handleResize: (event) => {
				_.ctx.emitter.emit(_.ctx.events.window.resize, { event });
			},

			// focus carousel on click for keyboard navigation
			handleCarouselClick: (event) => {
				// only focus if clicking on the carousel itself or viewport/track, not interactive elements
				const isInteractive = event.target.matches(INTERACTIVE_SELECTOR);
				if (!isInteractive) {
					_.carousel.focus({ preventScroll: true });
				}
			},

			// window focus/blur
			handleWindowFocus: (event) => {
				_.ctx.emitter.emit(_.ctx.events.window.hasFocus, {});
			},
			handleWindowBlur: (event) => {
				_.ctx.emitter.emit(_.ctx.events.window.lostFocus, {});
			},

			// carousel focus/blur (component-level)
			handleCarouselFocus: (event) => {
				// notify plugins/features about focus entering the carousel
				_.ctx.emitter.emit(_.ctx.events.carousel.hasFocus, {});
				// treat entering focus as user interaction (pause autoplay etc.)
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'focus', event });
			},
			handleCarouselBlur: (event) => {
				_.ctx.emitter.emit(_.ctx.events.carousel.lostFocus, {});
			},

			// visibility change
			handleVisibilityChange: () => {
				_.ctx.emitter.emit(_.ctx.events.window.visibilityChange, { hidden: document.hidden });
			},

			// user inputs that imply interaction (for autoplay pause)
			handleKeyDown: (event) => {
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'key', event });

				// core arrow key support only when carousel itself has focus
				// plugins handle their own keyboard events and call stopPropagation()
				if (['ArrowLeft', 'ArrowRight'].includes(event.key) && event.target === _.carousel) {
					event.preventDefault();
					const direction = event.key === 'ArrowLeft' ? -1 : 1;
					_.ctx.emitter.emit(_.ctx.events.keyboard.arrow, { direction, event });
				}
			},
			handleWheel: (event) => {
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'wheel', event });
			},

			// unified debounced handler for resize and orientation change
			unifiedResizeHandler: _.ctx.utils.debounce((event) => {
				const currentWidth = _.viewport?.clientWidth ?? window.innerWidth;
				// skip height-only changes (e.g., mobile Safari bottom bar show/hide)
				// only checked for window/orientation events; ResizeObserver already
				// did per-element early-out via _.observedWidths
				if (
					event !== undefined &&
					!(event instanceof ResizeObserverEntry) &&
					_.lastViewportWidth !== null &&
					currentWidth === _.lastViewportWidth
				) {
					return;
				}
				_.lastViewportWidth = currentWidth;
				_.handlers.handleResize(event);
			}, 4),

			// react to options changes by re-resolving the bounds element observation
			handleOptionsChanged: ({ currentOptions, previousOptions }) => {
				if (!currentOptions) return;
				if (
					previousOptions &&
					previousOptions.visibilityBoundsElement === currentOptions.visibilityBoundsElement
				) {
					return;
				}
				_.syncBoundsObservation();
			},
		};

		// unified resize observer: watches the viewport, and optionally the
		// visibility-bounds element when it differs from the viewport
		_.unifiedObserver = new ResizeObserver((entries) => {
			let changed = false;
			for (const entry of entries) {
				const w = entry?.contentRect?.width;
				if (w === undefined) continue;
				if (_.observedWidths.get(entry.target) === w) continue;
				_.observedWidths.set(entry.target, w);
				changed = true;
			}
			if (changed) _.handlers.unifiedResizeHandler(entries[0]);
		});

		_.init();
	}

	/**
	 * initializes viewport observer and calls bindEvents
	 */
	init() {
		const _ = this;

		// start observing the viewport for size changes
		if (_.viewport) {
			_.unifiedObserver.observe(_.viewport);
		}

		// initialize lastViewportWidth to current width
		_.lastViewportWidth = _.viewport?.clientWidth ?? window.innerWidth;

		_.bindEvents();

		// resolve and start observing the visibility bounds element if needed
		_.syncBoundsObservation();
	}

	/**
	 * Resolves the current visibilityBoundsElement option and updates which
	 * element is being observed for resize. Called on init and on options changes.
	 */
	syncBoundsObservation() {
		const _ = this;
		const options = _.ctx.store.getOptions();
		const resolved = _.ctx.utils.resolveVisibilityBoundsElement(
			options.visibilityBoundsElement,
			_.carousel,
			_.viewport
		);

		// no change needed
		if (resolved === _.boundsElement) return;

		// stop observing the previous bounds element (if it wasn't the viewport)
		if (_.boundsElement && _.boundsElement !== _.viewport) {
			_.unifiedObserver.unobserve(_.boundsElement);
			_.observedWidths.delete(_.boundsElement);
		}

		_.boundsElement = resolved;

		// start observing the new bounds element (if it's distinct from viewport)
		if (resolved && resolved !== _.viewport) {
			_.unifiedObserver.observe(resolved);
		}

		// trigger a recompute so widths.visibilityBoundsWidth/Offset get updated
		_.handlers.unifiedResizeHandler();
	}

	/**
	 * binds all event listeners for window, document, and carousel
	 */
	bindEvents() {
		const _ = this;
		const carousel = _.carousel;
		const handlers = _.handlers;

		// window resize and orientation change: both use the unified handler
		window.addEventListener('resize', handlers.unifiedResizeHandler);
		window.addEventListener('orientationchange', handlers.unifiedResizeHandler);

		// focus and blur events for window and carousel
		window.addEventListener('focus', handlers.handleWindowFocus);
		window.addEventListener('blur', handlers.handleWindowBlur);
		carousel.addEventListener('focus', handlers.handleCarouselFocus, true);
		carousel.addEventListener('blur', handlers.handleCarouselBlur, true);

		// visibility change
		document.addEventListener('visibilitychange', handlers.handleVisibilityChange);

		// user inputs that imply interaction (for autoplay pause)
		carousel.addEventListener('keydown', handlers.handleKeyDown, true);
		carousel.addEventListener('wheel', handlers.handleWheel, { passive: true });
		carousel.addEventListener('click', handlers.handleCarouselClick, true);

		// react to options changes so visibilityBoundsElement updates rebind the observer
		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, handlers.handleOptionsChanged);
	}

	/**
	 * cleans up events and observers
	 */
	destroy() {
		const _ = this;

		// remove window resize and orientation change
		window.removeEventListener('resize', _.handlers.unifiedResizeHandler);
		window.removeEventListener('orientationchange', _.handlers.unifiedResizeHandler);

		// remove focus/blur events for window
		window.removeEventListener('focus', _.handlers.handleWindowFocus);
		window.removeEventListener('blur', _.handlers.handleWindowBlur);

		// remove focus/blur events for carousel
		_.carousel.removeEventListener('focus', _.handlers.handleCarouselFocus, true);
		_.carousel.removeEventListener('blur', _.handlers.handleCarouselBlur, true);

		// remove visibility change
		document.removeEventListener('visibilitychange', _.handlers.handleVisibilityChange);

		// remove user input listeners
		_.carousel.removeEventListener('keydown', _.handlers.handleKeyDown, true);
		_.carousel.removeEventListener('wheel', _.handlers.handleWheel, { passive: true });
		_.carousel.removeEventListener('click', _.handlers.handleCarouselClick, true);

		// unsubscribe from options changes
		_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.handleOptionsChanged);

		// disconnect the unified resize observer
		if (_.unifiedObserver) {
			_.unifiedObserver.disconnect();
			_.unifiedObserver = null;
		}

		_.boundsElement = null;
		_.observedWidths = null;
	}
}
